import { useEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import SpaRoundedIcon from '@mui/icons-material/SpaRounded'
import { getDiagnosisFromCache } from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisCache'
import { DIAGNOSIS_UPDATE_EVENT } from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisManager'

/**
 * Short label chip for a completed appointment row. The label is role-aware and always carries an
 * abbreviation prefix:
 *   - Dx (Diagnosis) / Cc (Complaint)  → doctor consultations
 *   - Tx (Therapy) / PC (Presenting concern) → therapist sessions
 *
 * Reads exclusively from the IndexedDB cache — the whole day's labels are primed in ONE bulk call
 * by ConsultsTab (see apptDiagnosisManager.primeDiagnosesForDay), so there is NO per-row network
 * request here. The chip re-reads the cache when the day is primed, and self-gates to the mentor
 * flow + events likely to have documentation. Renders nothing when there is no label.
 */

const KIND_CONFIG = {
  dx: {
    abbr: 'Dx',
    full: 'Diagnosis',
    Icon: LocalHospitalRoundedIcon,
    fg: '#3730a3',
    bg: 'rgba(99, 102, 241, 0.12)',
  },
  cc: {
    abbr: 'Cc',
    full: 'Complaint',
    Icon: ChatBubbleOutlineRoundedIcon,
    fg: '#9a3412',
    bg: 'rgba(249, 115, 22, 0.12)',
  },
  pc: {
    abbr: 'PC',
    full: 'Presenting concern',
    Icon: ChatBubbleOutlineRoundedIcon,
    fg: '#9a3412',
    bg: 'rgba(249, 115, 22, 0.12)',
  },
  tx: {
    abbr: 'Tx',
    full: 'Therapy',
    Icon: SpaRoundedIcon,
    fg: '#115e59',
    bg: 'rgba(20, 184, 166, 0.14)',
  },
}

export default function EventDiagnosisChip({ bookedSlot, sessionStatus }) {
  const eventId = bookedSlot?.id
  const isMentorFlow =
    typeof window !== 'undefined' && window.location.pathname.includes('/mentor-home/')
  const eligible = Boolean(
    eventId &&
      isMentorFlow &&
      (sessionStatus === 'completed' || (bookedSlot?.reportContentSize || 0) > 0),
  )

  // undefined = unknown/loading, { label: '', kind } = checked-none, { label, kind } = has label
  const [entry, setEntry] = useState(undefined)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!eligible) return undefined

    let cancelled = false
    const readCache = () => {
      getDiagnosisFromCache(eventId).then((rec) => {
        if (cancelled || !mountedRef.current || !rec) return
        setEntry({ label: rec.label || '', kind: rec.kind || 'dx' })
      })
    }

    readCache()
    // Re-read when the day's labels get primed into the cache.
    const onUpdate = () => readCache()
    window.addEventListener(DIAGNOSIS_UPDATE_EVENT, onUpdate)

    // Safety net: never leave a permanent skeleton if priming fails / never runs.
    const timeoutId = setTimeout(() => {
      if (!cancelled && mountedRef.current) {
        setEntry((prev) => (prev === undefined ? { label: '', kind: 'dx' } : prev))
      }
    }, 12000)

    return () => {
      cancelled = true
      window.removeEventListener(DIAGNOSIS_UPDATE_EVENT, onUpdate)
      clearTimeout(timeoutId)
    }
  }, [eligible, eventId])

  if (!eligible) return null

  // Loading: subtle skeleton until the day is primed.
  if (entry === undefined) {
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 54,
          height: 12,
          borderRadius: 6,
          backgroundColor: 'rgba(100, 116, 139, 0.12)',
        }}
      />
    )
  }

  if (!entry.label) return null

  const cfg = KIND_CONFIG[entry.kind] || KIND_CONFIG.dx
  const { Icon } = cfg

  return (
    <Tooltip title={`${cfg.full}: ${entry.label}`} arrow>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          maxWidth: 220,
          fontSize: '11px',
          fontWeight: 600,
          color: cfg.fg,
          backgroundColor: cfg.bg,
          padding: '1px 7px 1px 5px',
          borderRadius: '6px',
          lineHeight: 1.5,
        }}
      >
        <Icon style={{ fontSize: 13, flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cfg.abbr}: {entry.label}
        </span>
      </span>
    </Tooltip>
  )
}
